import { NgIf, CommonModule } from '@angular/common'
import { Component, inject, Output, EventEmitter, OnInit } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  CTC,
  EmployeeSalary,
  ExtendedSalaryComponent,
  StatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.state'
import { combineLatest, filter, firstValueFrom, take, tap } from 'rxjs'
import {
  selectEmployeeSalary,
  selectRevisedSalary
} from '../../../../store/employee-salary/employee-salary.reducers'
import { User, UserRole } from '../../../../store/auth/auth.state'
import { selectSelectedUser } from '../../../../store/user/user.reducer'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'

export interface SalaryComparisonItem {
  itemName: string
  category: 'CTC' | 'Salary Component' | 'Other Component' | 'Statutory Compliance'
  previousValue: number | string | Date | null
  revisedValue: number | string | Date | null
  difference?: number
  isChanged: boolean
  percentageChange?: number
  isNumeric: boolean
  isComplianceList: boolean
  addedCompliances?: string[]
  removedCompliances?: string[]
}

@Component({
  selector: 'app-revised-salary-summary',
  imports: [NgIf, FormsModule, ReactiveFormsModule, CommonModule, CapitalizePipe],
  templateUrl: './revised-salary-summary.component.html',
  styleUrl: './revised-salary-summary.component.scss'
})
export class RevisedSalarySummaryComponent implements OnInit {
  private store = inject(Store)
  @Output() saveButtonState = new EventEmitter<boolean>()

  employeeSalary: EmployeeSalary | null = null
  revisedSalary: EmployeeSalary | null = null
  salaryComparison: SalaryComparisonItem[] = []
  user!: User

  async ngOnInit() {
    this.user = await firstValueFrom(
      this.store.pipe(
        select(selectSelectedUser),
        filter((user): user is User => !!user),
        take(1)
      )
    )

    combineLatest([this.store.select(selectEmployeeSalary), this.store.select(selectRevisedSalary)])
      .pipe(
        take(1),
        tap(([employeeSalary, revisedSalary]) => {
          if (employeeSalary && revisedSalary) {
            this.employeeSalary = employeeSalary
            this.revisedSalary = revisedSalary
            this.salaryComparison = this.generateSalaryComparison(employeeSalary, revisedSalary)
          } else {
            this.employeeSalary = null
            this.revisedSalary = null
            this.salaryComparison = []
          }
          this.saveButtonState.emit(!(this.salaryComparison.length > 0))
        })
      )
      .subscribe()

    console.log('salaryComparison', this.salaryComparison)
    console.log('employeeSalary', this.employeeSalary)
    console.log('revisedSalary', this.revisedSalary)
  }

  generateSalaryComparison(prev: EmployeeSalary, rev: EmployeeSalary): SalaryComparisonItem[] {
    const comparison: SalaryComparisonItem[] = []

    // 0. Compare CTC Dates
    this.compareDates(
      comparison,
      'Effective From',
      prev.ctc.effectiveFrom,
      rev.ctc.effectiveFrom,
      'CTC'
    )
    this.compareDates(comparison, 'Payout From', prev.ctc.payoutFrom, rev.ctc.payoutFrom, 'CTC')

    // 1. Compare CTC Details
    this.compareValues(comparison, 'CTC Amount', prev.ctc.ctc, rev.ctc.ctc, 'CTC')

    // 2. Compare Fixed Salary Data Components
    this.compareValues(
      comparison,
      'Basic',
      prev.salaryData.basic,
      rev.salaryData.basic,
      'Salary Component'
    )
    if (this.isContractor(this.user)) {
      this.compareValues(
        comparison,
        'HRA',
        prev.salaryData.hra,
        rev.salaryData.hra,
        'Salary Component'
      )
    }

    this.compareValues(comparison, 'PF', prev.salaryData.pf, rev.salaryData.pf, 'Salary Component')
    this.compareValues(
      comparison,
      'Special Allowance',
      prev.salaryData.specialAllowance,
      rev.salaryData.specialAllowance,
      'Salary Component'
    )

    // 3. Compare Other Components (Dynamic)
    this.compareOtherComponents(
      comparison,
      prev.salaryData.otherComponents,
      rev.salaryData.otherComponents
    )

    // 4. Compare Statutory Compliances (as lists)
    this.compareStatutoryCompliances(
      comparison,
      prev.statutoryCompliances,
      rev.statutoryCompliances
    )

    //return comparison.filter(item => item.isChanged === true)
    return comparison
  }

  // Helper to compare non-numeric/date values
  compareDates(
    comparison: SalaryComparisonItem[],
    itemName: string,
    previousValue: string | Date | null | undefined,
    revisedValue: string | Date | null | undefined,
    category: SalaryComparisonItem['category']
  ): void {
    // Determine display/comparison values consistently
    const formatForComparison = (val: string | Date | null | undefined): string => {
      if (val instanceof Date) return val.toISOString() // Use ISO for reliable comparison
      if (val === null || val === undefined) return 'null' // Distinguish null from empty string ""
      return String(val) // Keep other strings as they are
    }

    const prevComp = formatForComparison(previousValue)
    const revComp = formatForComparison(revisedValue)

    if (prevComp === revComp) return // No change based on consistent comparison

    // Prepare values for storage in the item (keep original type if possible)
    const prevStoredValue = previousValue ?? null
    const revStoredValue = revisedValue ?? null

    const item: SalaryComparisonItem = {
      itemName,
      category,
      // Store original Date object, string, number, or null
      previousValue: prevStoredValue,
      revisedValue: revStoredValue,
      isNumeric: false,
      isComplianceList: false,
      isChanged: true
    }
    comparison.push(item)
  }

  // Helper to compare static salary items
  compareValues(
    comparison: SalaryComparisonItem[],
    itemName: string,
    previousValue: number,
    revisedValue: number,
    category: SalaryComparisonItem['category']
  ): void {
    // if (previousValue === revisedValue) return

    const item: Partial<SalaryComparisonItem> = {
      itemName,
      category,
      previousValue,
      revisedValue,
      isNumeric: true,
      isComplianceList: false
    }

    item.difference = revisedValue - previousValue
    item.isChanged = item.difference !== 0 ? true : false
    if (previousValue !== 0) {
      item.percentageChange = parseFloat(((item.difference / previousValue) * 100).toFixed(2))
    } else if (revisedValue !== 0) {
      item.percentageChange = 100
    } else {
      item.percentageChange = 0
    }

    comparison.push(item as SalaryComparisonItem)
  }

  // Helper to compare the dynamic 'otherComponents' (dynamic salary components) array
  compareOtherComponents(
    comparison: SalaryComparisonItem[],
    prevComponents: ExtendedSalaryComponent[],
    revComponents: ExtendedSalaryComponent[]
  ): void {
    const prevMap = new Map(prevComponents.map(c => [c.id, c]))
    const revMap = new Map(revComponents.map(c => [c.id, c]))

    // Check existing/modified/removed components
    prevMap.forEach((prevComp, id) => {
      const revComp = revMap.get(id)
      const itemNameBase = prevComp.name
      if (revComp) {
        console.log('prevComp', prevComp)
        // Component exists in both, compare amounts
        if (prevComp.componentType === 'customDeductions') {
          this.compareValues(
            comparison,
            `${itemNameBase} (Employee Contribution)`,
            prevComp.employeeContribution ?? 0,
            revComp.employeeContribution ?? 0,
            'Other Component'
          )
        }
        this.compareValues(
          comparison,
          `${itemNameBase} (Employer Contribution)`,
          prevComp.employerContribution ?? 0,
          revComp.employerContribution ?? 0,
          'Other Component'
        )
      } else {
        // Component was removed
        if (prevComp.componentType === 'customDeductions') {
          comparison.push({
            itemName: `${itemNameBase} (Employee Contribution)`,
            category: 'Other Component',
            previousValue: prevComp.employeeContribution ?? 0,
            revisedValue: 'Removed',
            isNumeric: false,
            isComplianceList: false,
            isChanged: false
          })
        }
        comparison.push({
          itemName: `${itemNameBase} (Employer Contribution)`,
          category: 'Other Component',
          previousValue: prevComp.employerContribution ?? 0,
          revisedValue: 'Removed',
          isNumeric: false,
          isComplianceList: false,
          isChanged: false
        })
      }
    })

    // Check for added components
    revMap.forEach((revComp, id) => {
      if (!prevMap.has(id)) {
        // Component was added
        const itemNameBase = revComp.name
        // Add entries for added contributions if they exist
        if (revComp.componentType === 'customDeductions') {
          comparison.push({
            itemName: `${itemNameBase} (Employee Contribution)`,
            category: 'Other Component',
            previousValue: 'N/A',
            revisedValue: revComp.employeeContribution ?? 0,
            isNumeric: false,
            isComplianceList: false,
            isChanged: true,
            difference: 100
          })
        }
        comparison.push({
          itemName: `${itemNameBase} (Employer Contribution)`,
          category: 'Other Component',
          previousValue: 'N/A',
          revisedValue: revComp.employerContribution ?? 0,
          isNumeric: false,
          isComplianceList: false,
          isChanged: true,
          difference: 100
        })
      }
    })
  }

  // Helper to get enabled statutory compliance keys as user-friendly strings
  getEnabledCompliances(compliances: StatutoryCompliances): string[] {
    const enabled: string[] = []
    if (compliances.pf) enabled.push('PF')
    if (compliances.eps) enabled.push('EPS')
    if (compliances.employerCapping) enabled.push('PF Employer Capping')
    if (compliances.esi) enabled.push('ESI')
    if (compliances.lwf) enabled.push('LWF')
    if (compliances.pt) enabled.push('PT')
    return enabled.sort()
  }

  // Helper to compare statutory compliances
  compareStatutoryCompliances(
    comparison: SalaryComparisonItem[],
    prev: StatutoryCompliances,
    rev: StatutoryCompliances
  ): void {
    const prevEnabled = this.getEnabledCompliances(prev)
    const revEnabled = this.getEnabledCompliances(rev)
    const prevSet = new Set(prevEnabled)
    const revSet = new Set(revEnabled)

    // Find items in revEnabled but not in prevEnabled (Added)
    const added = revEnabled.filter(item => !prevSet.has(item))

    // Find items in prevEnabled but not in revEnabled (Removed)
    const removed = prevEnabled.filter(item => !revSet.has(item))

    const isChanged = added.length > 0 || removed.length > 0

    // Only add the row if there's an actual change in compliances
    if (isChanged) {
      comparison.push({
        itemName: 'Statutory Compliances',
        category: 'Statutory Compliance',
        previousValue: prevEnabled.length > 0 ? prevEnabled.join(', ') : 'None',
        revisedValue: revEnabled.length > 0 ? revEnabled.join(', ') : 'None',
        isNumeric: false,
        isComplianceList: true,
        isChanged: true,
        addedCompliances: added,
        removedCompliances: removed
        // difference and percentageChange are not applicable/calculated here
      })
    } // Optional: If you always want to show the compliance row even if unchanged
    /* else {
         comparison.push({
           itemName: 'Statutory Compliances',
           category: 'Statutory Compliance',
           previousValue: prevEnabled.length > 0 ? prevEnabled.join(', ') : 'None',
           revisedValue: revEnabled.length > 0 ? revEnabled.join(', ') : 'None',
           isNumeric: false,
           isComplianceList: true,
           isChanged: false,
           addedCompliances: [],
           removedCompliances: []
         });
     } */
  }

  isContractor(employee: any): boolean {
    return employee?.userRoles?.some((r: UserRole) => r?.role?.name === 'Contractor') || false
  }
}
